import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@folio/components';
import { useKorintConfig } from 'korint-sdk/react';
import type { Language } from 'korint-sdk/react';
import { Check, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { persistLanguage } from '../../i18n';
import { Text } from '@folio/components/ui/text';

const LANGUAGE_LABELS: Record<Language, string> = {
  fr: 'FR',
  en: 'EN',
};

export function LanguageToggle() {
  const { korintConfig } = useKorintConfig();
  const availableLanguages = korintConfig?.uniqAppConfig?.availableLanguages;
  const { i18n } = useTranslation();

  if (!availableLanguages || availableLanguages.length < 2) {
    return;
  }

  const currentLanguage: Language =
    i18n.language in LANGUAGE_LABELS ? (i18n.language as Language) : 'fr';

  const handleSelect = async (language: Language) => {
    if (language === currentLanguage) return;
    await i18n.changeLanguage(language);
    persistLanguage(language);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex items-center gap-2 p-2 text-sm rounded hover:bg-gray-50 w-full"
        aria-label="Language selector"
      >
        <Globe className="h-4 w-4 text-gray-500" />
        <Text inline weight="medium">
          {LANGUAGE_LABELS[currentLanguage]}
        </Text>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {availableLanguages.map(language => (
          <DropdownMenuItem
            key={language}
            onSelect={() => void handleSelect(language)}
            className="flex items-center justify-between gap-4 min-w-24"
          >
            <span>{LANGUAGE_LABELS[language]}</span>
            {language === currentLanguage && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
